import { Injectable } from '@angular/core';
import { EPTableStyle } from '@ep/shared';
import { PortalTheme } from '../models/application-profile.model';

export interface ApplyThemeOptions {
  advancedView?: boolean;
  overrideByPrimary?: boolean;
  root?: HTMLElement;
}

@Injectable({
  providedIn: 'root',
})
export class PortalThemeService {
  applyTheme(theme: PortalTheme, options?: ApplyThemeOptions): void {
    if (!theme) {
      return;
    }

    const advancedView = options?.advancedView ?? false;
    const overrideByPrimary = options?.overrideByPrimary ?? false;
    const root = options?.root ?? document.documentElement;

    root.style.setProperty('--primary', theme.primary);

    root.style.setProperty('--text--color-default', theme.defaultTextColor);

    root.style.setProperty('--main-content-bg', theme.backgroundColor);

    root.style.setProperty('--header-bg-primary', theme.header?.backgroundColor);

    root.style.setProperty('--header-primary', theme.header?.primary);
    if (advancedView) {
      theme.header.itemsColor = theme.header.itemsColor;
    } else if (overrideByPrimary) {
      theme.header.itemsColor = theme.primary;
    }
    root.style.setProperty('--header-items-color', theme.header.itemsColor);

    root.style.setProperty('--header-text-color', theme.header.textColor);

    root.style.setProperty('--sidebar-bg-primary', theme.sidebar?.backgroundColor?.primary);

    if (advancedView) {
      theme.sidebar.backgroundColor.active = theme.sidebar?.backgroundColor?.active;
    } else if (overrideByPrimary) {
      theme.sidebar.backgroundColor.active = theme.primary;
    }
    root.style.setProperty('--sidebar-bg-active', theme.sidebar.backgroundColor.active);

    if (advancedView) {
      theme.sidebar.backgroundColor.dropdown = theme.sidebar?.backgroundColor?.dropdown;
    } else if (overrideByPrimary) {
      theme.sidebar.backgroundColor.dropdown = this.adjustHexColorLightness(theme.primary, 15);
    }
    root.style.setProperty('--sidebar-bg-dropdown', theme.sidebar.backgroundColor.dropdown);

    if (advancedView) {
      theme.sidebar.backgroundColor.secondary = theme.sidebar?.backgroundColor?.secondary;
    } else if (overrideByPrimary) {
      theme.sidebar.backgroundColor.secondary = this.adjustHexColorLightness(theme.primary, 20);
    }
    root.style.setProperty('--sidebar-bg-secondary', theme.sidebar.backgroundColor.secondary);

    root.style.setProperty('--sidebar-text-color', theme.sidebar?.textColor);
    root.style.setProperty('--sidebar-logo-bg', theme.sidebar?.logoBackgroundColor);

    if (advancedView) {
      theme.form.header.primaryBackgroundColor = theme.form.header.primaryBackgroundColor;
    } else if (overrideByPrimary) {
      theme.form.header.primaryBackgroundColor = theme.primary;
    }
    root.style.setProperty('--form-header-bg-primary', theme.form.header?.primaryBackgroundColor);

    if (advancedView) {
      theme.form.header.itemsColor = theme.form.header.itemsColor;
    } else if (overrideByPrimary) {
      theme.form.header.itemsColor = theme.primary;
    }
    root.style.setProperty('--form-header-items-color', theme.form.header.itemsColor);

    root.style.setProperty('--form-header-items-text-color', theme.form.header.itemsTextColor);

    root.style.setProperty('--form-header-bg-color', theme.form?.backgroundColor);

    root.style.setProperty('--form-header-text-color', theme.form?.header?.textColor);
    root.style.setProperty('--form-bg-color', theme.form?.backgroundColor);

    root.style.setProperty('--dashboard-card-bg', theme.dashboard?.cardBackgroundColor);
    root.style.setProperty('--dashboard-text--color', theme.dashboard.coloredCardTextColor);
    root.style.setProperty('--dashboard-header-bg', theme.dashboard?.headerBackgroundColor);
    root.style.setProperty('--dashboard-header-text-color', theme.dashboard?.headerTextColor);

    root.style.setProperty('--table-primary-text', theme.table?.body?.textColor);
    root.style.setProperty('--table-sub-header-text', theme?.table.subHeaderTextColor);

    root.style.setProperty('--table-bg-primary', theme.table?.header?.backgroundColor);
    root.style.setProperty('--table-bg-primary-text', theme.table?.header?.textColor);
    root.style.setProperty('--table-bg-subheader', theme.table?.subHeaderBackgroundColor);
    root.style.setProperty('--table-bg-subheader-text', theme.table?.subHeaderTextColor);
    root.style.setProperty('--table-bg-body-primary', theme.table?.body?.primaryBackgroundColor);
    root.style.setProperty('--table-bg-body-primary-text', theme.table?.body?.textColor);
    root.style.setProperty('--table-bg-body-secondary', theme.table?.body?.secondaryBackgroundColor);
    root.style.setProperty('--table-bg-status-primary', theme.table?.statusColors?.primary);
    root.style.setProperty('--table-bg-status-secondary', theme.table?.statusColors?.secondary);
    root.style.setProperty('--table-bg-status-warning', theme.table?.statusColors?.warning);
    root.style.setProperty('--table-bg-status-danger', theme.table?.statusColors?.danger);
  }

  mergeWithDefaultTheme(theme?: Partial<PortalTheme> | null): PortalTheme {
    return this.deepMerge(this.getDefaultTheme(), theme);
  }

  private deepMerge<T>(base: T, override: any): T {
    if (override === null || override === undefined) {
      return base;
    }
    const result: any = { ...base };
    for (const key of Object.keys(override)) {
      const overrideValue = override[key];
      const baseValue = (base as any)?.[key];
      if (
        overrideValue &&
        typeof overrideValue === 'object' &&
        !Array.isArray(overrideValue) &&
        baseValue &&
        typeof baseValue === 'object'
      ) {
        result[key] = this.deepMerge(baseValue, overrideValue);
      } else if (overrideValue !== undefined) {
        result[key] = overrideValue;
      }
    }
    return result;
  }

  getTableStyle(theme: PortalTheme, options?: ApplyThemeOptions): EPTableStyle {
    const advancedView = options?.advancedView ?? false;
    const overrideByPrimary = options?.overrideByPrimary ?? false;

    if (advancedView) {
      theme.table.header.backgroundColor = theme.table.header.backgroundColor;
    } else if (overrideByPrimary) {
      theme.table.header.backgroundColor = theme!.primary!;
    }

    return {
      'custom-header-background': theme?.table.header.backgroundColor || '',
      'custom-table-subheader-background': theme?.table.subHeaderBackgroundColor || '',
      'custom-table-subheader-text-color': theme?.table.subHeaderTextColor || '',
      'custom-header-text-color': theme?.table.header.textColor || '',
      'custom-table-body-primary-background': theme?.table.body.primaryBackgroundColor || '',
      'custom-table-body-secondary-background': theme?.table.body.secondaryBackgroundColor || '',
      'custom-table-body-text-color': theme?.table.body.textColor || '',
    };
  }

  adjustHexColorLightness(hexColor: string, percentage: number): string {
    hexColor = hexColor.startsWith('#') ? hexColor.slice(1) : hexColor;

    if (hexColor.length === 3) {
      hexColor = hexColor[0] + hexColor[0] + hexColor[1] + hexColor[1] + hexColor[2] + hexColor[2];
    }

    let r = parseInt(hexColor.substring(0, 2), 16);
    let g = parseInt(hexColor.substring(2, 4), 16);
    let b = parseInt(hexColor.substring(4, 6), 16);

    const adjustment = 255 * (percentage / 100);

    r = Math.min(255, Math.max(0, r + adjustment));
    g = Math.min(255, Math.max(0, g + adjustment));
    b = Math.min(255, Math.max(0, b + adjustment));

    const toHex = (c: number) => {
      const hex = Math.round(c).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };

    return '#' + toHex(r) + toHex(g) + toHex(b);
  }

  getDefaultTheme(): PortalTheme {
    return {
      primary: '#3b8cc7',
      backgroundColor: '#fff',
      defaultTextColor: '#000',
      sidebar: {
        backgroundColor: {
          primary: '#213246',
          secondary: '#30a0e6',
          active: '#3b8cc7',
          dropdown: '#3b8cc7ba',
        },
        textColor: '#FFF',
        logoBackgroundColor: '#1e2235',
      },
      header: {
        primary: '#3b8cc7',
        backgroundColor: '#f8f9fa',
        itemsColor: '#3b8cc7',
        textColor: '#fff',
      },
      dashboard: {
        coloredCardTextColor: '#fff',
        sales: '#3b8cc7',
        loyalty: '#6fc066',
        rewards: '#ffd340',
        reviews: '#363b57',
        cardBackgroundColor: '#fff',
        headerBackgroundColor: '#f8f9fa',
        headerTextColor: '#000',
      },
      table: {
        header: {
          backgroundColor: '#3b8cc7',
          textColor: '#fff',
        },
        subHeaderBackgroundColor: '#f8f9fa',
        subHeaderTextColor: '#000',
        statusColors: {
          primary: '#3b8cc7',
          secondary: '#1e2235',
          warning: '#ffd340',
          danger: '#E71D36',
        },
        body: {
          primaryBackgroundColor: '#fff',
          secondaryBackgroundColor: '#fcfcfc',
          textColor: '#000',
        },
      },
      form: {
        header: {
          primaryBackgroundColor: '#3b8cc7',
          secondaryBackgroundColor: '#FFF',
          textColor: '#fff',
          itemsColor: '#3b8cc7',
          itemsTextColor: '#fff',
        },
        backgroundColor: '#fff',
      },
    };
  }
}
